// >>> BREACH-1 — WHAT A BROWSER MAY STILL BE HOLDING WHEN THE PERSON CHANGES. <<<
//
// THE INCIDENT, 2026-08-20, reproduced by the owner: she signed up with a random new address
// (`katy@hotmail.com`), typed the name "Katie", **no birth-details step ever appeared**, and she
// landed in "Lashonda's room" — her OWN prior seat's full name, birth date and life path — on a
// brand-new account.
//
// >>> THE CAUSE, AND IT IS ONE DEFECT WITH TWO SYMPTOMS: THE PROFILE HYDRATE WAS A ONE-WAY WRITE.
// <<< App.tsx read the member's profile on every `member` change and wrote `roomProfile` only on
// success:
//
//     if (p && p.birthDate) { setRoomProfile(...); setBirthChart(...); }   // and NO else
//
// So when the identity changed to an account with no birth date, **nothing ran** and `roomProfile`
// went on holding the PREVIOUS person's profile. From there both halves of her report follow from
// that one value:
//
//   * `hasRoom: Boolean(roomProfile)` was TRUE, so `showOnboarding` returned false and the
//     birth-details step never opened — the sacred flow skipped.
//   * My Room renders `profile={roomProfile}`, so the previous person's name, birth date and life
//     path painted on the new account's screen.
//
// **THE SERVER WAS NEVER WRONG AND THAT IS MEASURED RATHER THAN ASSUMED** (see DECISIONS BREACH-1):
// `handleRoomStateGet` and `handleProfile` both resolve the row from `readSignedSession` and
// `getRoomState` carries `WHERE user_id = $1` — the session's own id — so no member's rows can be
// served to another's account. The new seat owned ZERO rows in every table and its only two
// `gate_log` rows carry its own id. **The exposure is a browser painting what that same browser
// already held, and it is bounded by the device.** That is a smaller incident than a server leak
// and it is NOT a small one: sign-out did not clear it either, so on a SHARED DEVICE — a family
// tablet, a friend's phone — one person signing out and the next signing up in the same tab is
// exactly the exposing sequence.
//
// THIS MODULE IMPORTS NOTHING, which is the entry requirement that lets `scripts/breach-1.mjs` LOAD
// and RUN every clause below rather than reading it (R146: reach is transitive).

// >>> WHO THIS SESSION IS, AS ONE STABLE STRING. <<<
//
// The id when there is one, the folded address when there is not. **Both, because the two doors mint
// different shapes:** the Google path resolves a row id and the email path signs the address, and a
// law that knew only one of them would answer "same person" across a real change on the other
// (CRYSTAL's own measured failure: a session minted from `{ userId }` while the resolver reads the
// EMAIL, so it signed perfectly and resolved to nobody).
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** `Number(true)` is 1 and `String(123)` is "123", so
// an id that is not a real value must not become one; and the address is folded and trimmed because
// `ensureUser` matches `lower(btrim(email))` and two spellings of one person is two people here.
export function identityKey(member) {
  if (!member || typeof member !== "object") return "";
  const id = member.id;
  if (typeof id === "number" && Number.isFinite(id) && id > 0) return `id:${id}`;
  if (typeof id === "string" && /^[0-9]+$/.test(id.trim()) && id.trim() !== "0") return `id:${id.trim()}`;
  const email = member.email;
  if (typeof email === "string" && email.trim()) return `em:${email.trim().toLowerCase()}`;
  return "";
}

// >>> THE CHANGE THAT MUST CLEAR THE SCREEN. <<<
//
// Signed out -> signed in, signed in -> signed out, and **one person -> a different person**, which
// is the one the incident travelled on: she never signed out, she signed UP, so `member` went from a
// real person straight to a different real person with no null in between. A law written as
// "cleared on sign-out" would not have fired at all.
//
// A change from an UNREADABLE identity to the same unreadable one is not a change (both ""), so a
// re-render with no session cannot wipe a form somebody is typing into.
export function identityChanged(previous, next) {
  return identityKey(previous) !== identityKey(next);
}

// >>> WHAT THE PROFILE READ MAY DO TO THE SCREEN: THREE ANSWERS, NEVER TWO (R18). <<<
//
// `hold`  — the read has not landed, or it REFUSED. Nothing was learned, so nothing is decided. This
//           is the arm the naive repair loses: clearing on a refused read wipes a real member's room
//           to a blank shell on one bad minute of network, which is the opposite failure and the one
//           she would report as her room being deleted.
// `clear` — the read LANDED and this account has no room. **This is the arm that did not exist**,
//           and its absence is the whole incident.
// `set`   — the read landed and carries a birth date.
//
// **IT DOES NOT RE-IMPLEMENT THE BIRTH-DATE TEST.** `roomIsMissing` in `src/lib/profile-read.ts` has
// been the one answer to *does she have a room* since R71 — and R165 is right that nothing in the
// product was calling it, which is exactly how App.tsx came to hand-roll a fourth spelling. The two
// facts arrive as PARAMETERS (R46's locked-door shape, CLOSET-1's `closetSendVerdict`), so the call
// site passes `answer.known` and `roomIsMissing(answer)` and there is still only ONE place in this
// house that knows a room is a profile with a BIRTH DATE and never a profile row (ITEM 10's line,
// paid for a fourth time by this incident).
export function roomProfileAction({ known, roomMissing } = {}) {
  if (known !== true) return "hold";
  return roomMissing === true ? "clear" : "set";
}

// >>> WHAT A NEW PERSON MAY NOT INHERIT. <<<
//
// The roster of member-derived slots the shell must empty when the identity changes. It is DECLARED
// rather than left to whoever edits the reset next, and it is held by `scripts/breach-1.mjs` against
// the shell's own reset body, so a slot added here without being cleared is RED and a slot cleared
// without being declared is RED (R11/R31, both directions).
//
// **A SLOT IS ON THIS LIST WHEN ITS VALUE CAME FROM A MEMBER-SCOPED READ OR A MEMBER'S OWN TYPING.**
// `birthChart` is on it because the hydrate pours a profile into it; `signedInAs` because it names a
// room and an address; `submittedChart` because it is the chart a reading was cast on.
export const MEMBER_SCOPED_SLOTS = Object.freeze([
  "roomProfile",
  "submittedChart",
  "birthChart",
  "signedInAs",
  "profileChecked",
]);

// >>> AND THE RESET IS ONE FUNCTION WITH TWO CALLERS, WHICH IS THE HALF THAT KEEPS IT FIXED. <<<
//
// Sign-out and identity-change are the same act from the screen's point of view — the person in
// front of it is no longer the person the screen is about — and two spellings of *empty this screen*
// is how one of them comes to be missed (R28/R29). `logOut` cleared `member` and `pendingPage` and
// **nothing else**, which is why signing out and signing up in the same tab exposed the previous
// person; it calls this now, and so does the identity guard.
//
// The setters arrive as one object so this stays a pure law a gate can DRIVE with recorders rather
// than a block of `setX(null)` inside an eleven-thousand-line component that nothing can reach
// (R8/R95).
// ================================================================================================
// >>> **AND THE RESET WIPED THE ONE THING A STRANGER HAD JUST HANDED THE HOUSE, WHICH IS L10's
// CONTINUITY LAW BROKEN BY THIS ONE (FF1-BRIDGE, 2026-08-23).** <<<
//
// **MEASURED AT COORDINATES RATHER THAN REASONED ABOUT (R25/R180):** a funnel visitor taps *Finish
// this reading*, the bridge pours her details into `birthChart`, she signs up — and **signing in IS
// an identity change** (nobody -> her), so the guard above fires and empties the form she is looking
// at. The walk read `birthDate: ""` and `time: ["", "", "AM"]` on the create-room box, twice.
//
// **AND THE SENTENCE SURVIVED THE DATA, WHICH IS WHAT MAKES IT THE SHARP VERSION.** `prefilled` is
// not a member-scoped slot and is not on the roster, so **the house went on saying it had kept her
// details over an empty form** — on the one screen it asks a stranger to trust, seconds after an
// advertisement was paid for to bring her to it.
//
// **`birthChart` IS NOT COMING OFF THE ROSTER, AND THAT IS THE POINT.** It is on it because the
// hydrate pours a profile into it, and a stranger meeting the previous member's birth date IS the
// incident this file exists for. What is restored instead is the ONE thing that is provably hers.
//
// >>> **THE DISCRIMINATOR IS THE ADDRESS, AND IT IS EXACT RATHER THAN CAUTIOUS.** <<< The stash is
// `sessionStorage` — per tab — so *A came through a funnel, signed out, and B signed up in the same
// tab* would otherwise pour A's birth date into B's form: BREACH-1's own incident, arriving through
// the repair for it. The stash carries the address it was captured for, so it is re-applied ONLY to
// the person it belongs to, and no new state is needed to know that.
//
// **THE DIRECTION IS THE RULING (R19/R8-4):** a stash this cannot place is REFUSED, which costs her
// re-typing a birth date she can see is missing; pouring one it cannot place is a stranger's data on
// somebody else's screen, and that has no symptom at all.
//
// PRESENCE, NEVER COERCION (R53/R136/R137): both sides must be a real non-empty STRING, folded the
// way this house folds an address everywhere else, or two different people read as one.
export function funnelPrefillSurvives(stashed, member) {
  const stashedEmail = stashed && typeof stashed === "object" ? stashed.email : null;
  if (typeof stashedEmail !== "string" || !stashedEmail.trim()) return false;
  const memberEmail = member && typeof member === "object" ? member.email : null;
  if (typeof memberEmail !== "string" || !memberEmail.trim()) return false;
  return stashedEmail.trim().toLowerCase() === memberEmail.trim().toLowerCase();
}

export function resetMemberState(clearers = {}) {
  const cleared = [];
  const missed = [];
  for (const slot of MEMBER_SCOPED_SLOTS) {
    const fn = clearers[slot];
    // >>> EACH SLOT CLEARS ITSELF, AND THAT IS NOT A STYLE CHOICE. <<< A law that passed one empty
    // value would have to know that roomProfile empties to null, profileChecked to false and
    // birthChart to the FORM's own initial object — and handing the form a null is a crash on the
    // one screen this incident is about. The law owns WHICH slots must be emptied and the shell owns
    // what empty means for each, which is the only split where neither side can be wrong about the
    // other.
    if (typeof fn !== "function") { missed.push(slot); continue; }
    fn();
    cleared.push(slot);
  }
  // A slot with no clearer is a slot that would have survived the change. It is RETURNED rather than
  // thrown, because this runs during a render on the sign-out path and a throw there costs her the
  // whole screen — and it is asserted by the gate, which is where a roster gap belongs.
  return { cleared, missed };
}
